import React from 'react';

const GeneralAssemblyManagerTutorial: React.FC = () => {
  return (
    <div className="space-y-6">
      <section className="rounded-xl border-l-4 border-green-500 bg-gradient-to-br from-gray-50 to-gray-100 p-6 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-green-600">
          <span role="img" aria-label="target">🎯</span>
          Votre mission essentielle
        </h3>
        <div className="my-4 rounded-xl border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-emerald-100 p-5 shadow-sm">
          <h5 className="mb-2 text-lg font-bold text-emerald-700"><span role="img" aria-label="check mark">✅</span> VOTRE SEULE RESPONSABILITÉ</h5>
          <p className="leading-relaxed text-emerald-700">
            <strong>Saisir les dates d'Assemblées Générales de vos copropriétés.</strong>
            Cette information est cruciale pour permettre à l'équipe référent mutation location de planifier sa présence et ses actions.
          </p>
        </div>
        <div className="rounded-xl border-l-4 border-blue-400 bg-gradient-to-r from-blue-50 to-blue-100 p-5 font-medium text-blue-800 shadow-sm">
          <span role="img" aria-label="light bulb">💡</span> Pourquoi c'est important ? Le référent mutation location organise sa présence aux AG et prépare les documents à distribuer grâce à ces dates.
        </div>
      </section>

      <section className="rounded-xl border-l-4 border-green-500 bg-gradient-to-br from-gray-50 to-gray-100 p-6 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-green-600">
          <span role="img" aria-label="calendar">📅</span>
          Comment saisir les dates (très simple !)
        </h3>
        <h4 className="mt-2 mb-2 text-lg font-semibold text-blue-600">Étape 1 : Localiser votre copropriété</h4>
        <p className="leading-relaxed text-gray-700">Utilisez la recherche et trouvez vos immeubles. Si un immeuble manque, rapprochez-vous du référent mutation location pour les mises à jour.</p>
        <h4 className="mt-4 mb-2 text-lg font-semibold text-blue-600">Étape 2 : Saisir la date de la dernière AG</h4>
        <p className="leading-relaxed text-gray-700">Dans la colonne <strong>DERNIÈRE AG</strong>, sélectionnez la date de la dernière AG tenue.</p>
        <h4 className="mt-4 mb-2 text-lg font-semibold text-blue-600">Étape 3 : Saisir la date de la prochaine AG</h4>
        <p className="leading-relaxed text-gray-700">Dans la colonne <strong>DATE AG PRÉVUE</strong>, sélectionnez la date planifiée de la prochaine AG.</p>
        <div className="my-4 rounded-xl border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-emerald-100 p-5 shadow-sm">
          <h5 className="mb-2 text-lg font-bold text-emerald-700"><span role="img" aria-label="refresh">🔄</span> Mise à jour automatique</h5>
          <p className="text-sm leading-relaxed text-emerald-700">La colonne <strong>PRÉVISION PROCHAINE AG</strong> se met à jour automatiquement (≈ dans 12 mois après la dernière AG).</p>
        </div>
      </section>

      <section className="rounded-xl border-l-4 border-green-500 bg-gradient-to-br from-gray-50 to-gray-100 p-6 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-green-600">
          <span role="img" aria-label="filter">🔍</span>
          Comprendre les filtres (optionnel)
        </h3>
        <h4 className="mt-2 mb-2 text-lg font-semibold text-blue-600">Filtre "AG du mois"</h4>
        <ul className="mb-4 ml-6 list-disc space-y-1 text-gray-700">
          <li><strong>AG de ce mois</strong> : Affiche uniquement les AG de ce mois</li>
          <li><strong>AG mois prochain</strong> : Affiche les AG du mois suivant</li>
        </ul>
        <h4 className="mt-2 mb-2 text-lg font-semibold text-blue-600">Filtre "État des dates"</h4>
        <ul className="mb-4 ml-6 list-disc space-y-1 text-gray-700">
          <li><strong>Avec date AG</strong> : Les copropriétés avec une date d'AG prévue</li>
          <li><strong>Sans date AG</strong> : Les copropriétés sans date d'AG prévue</li>
        </ul>
        <div className="my-4 rounded-xl border-2 border-amber-500 bg-gradient-to-br from-amber-50 to-amber-100 p-5 shadow-sm">
          <h5 className="mb-2 text-lg font-bold text-amber-800"><span role="img" aria-label="emoji">⚠</span>️ AG "Non qualifiées"</h5>
          <p className="text-sm leading-relaxed text-amber-800">S'il manque la date de la prochaine AG, la copropriété est <strong>Non qualifiée</strong>. Renseignez la date pour permettre la planification du référent.</p>
        </div>
      </section>

      <section className="rounded-xl border-l-4 border-green-500 bg-gradient-to-br from-gray-50 to-gray-100 p-6 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-green-600">
          <span role="img" aria-label="info">ℹ️</span>
          À propos de la "Prévision prochaine AG"
        </h3>
        <div className="rounded border-l-4 border-red-500 bg-red-50 p-4">
          <p className="mb-2 font-semibold text-red-800">ATTENTION : Cette prévision est uniquement indicative</p>
        </div>
        <ul className="mt-3 ml-6 list-disc space-y-1 text-gray-700">
          <li>Le <strong>mois prévisionnel</strong> est calculé par rapport à la <strong>dernière AG</strong>.</li>
          <li>Si la dernière AG n'a pas eu lieu dans les <strong>6 mois</strong> de la clôture des comptes, la prévision peut être <span className="font-semibold text-red-600">fausse</span>.</li>
          <li>Cette prévision n'a <strong>aucune valeur légale</strong>.</li>
        </ul>
        <div className="mt-3 rounded border-l-4 border-yellow-500 bg-yellow-50 p-4">
          <p className="mb-2 font-semibold text-yellow-800">Veuillez remplir correctement :</p>
          <ul className="list-inside list-disc space-y-1 text-yellow-700">
            <li>Les dates des anciennes AG</li>
            <li>La nouvelle date d'AG prévue</li>
            <li>En tenant compte des règles légales</li>
          </ul>
        </div>
      </section>

      <section className="rounded-xl border-l-4 border-green-500 bg-gradient-to-br from-gray-50 to-gray-100 p-6 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-green-600">
          <span role="img" aria-label="tools">🛠️</span>
          Fonctionnalités supplémentaires
        </h3>
        <h4 className="mt-2 mb-2 text-lg font-semibold text-blue-600">Le bouton "Éditer Document AG"</h4>
        <p className="leading-relaxed text-gray-700">Préparez le document à poser sur les tables et la page dédiée au référent mutation location.</p>
        <h4 className="mt-4 mb-2 text-lg font-semibold text-blue-600">Export des données</h4>
        <p className="leading-relaxed text-gray-700">Le bouton <strong>Export Complet</strong> télécharge un Excel avec toutes vos copropriétés et leurs dates.</p>
      </section>

      <section className="rounded-xl border-l-4 border-green-500 bg-gradient-to-br from-gray-50 to-gray-100 p-6 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-green-600">
          <span role="img" aria-label="checklist">✅</span>
          Récapitulatif — À retenir
        </h3>
        <div className="my-2 rounded-xl border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-emerald-100 p-5 shadow-sm">
          <h5 className="mb-2 text-lg font-bold text-emerald-700"><span role="img" aria-label="fire">🔥</span> L'essentiel en 30 secondes</h5>
          <p className="text-sm leading-relaxed text-emerald-700">
            1) Trouvez vos copropriétés<br />
            2) Remplissez <strong>DERNIÈRE AG</strong> et <strong>DATE AG PRÉVUE</strong><br />
            3) La prévision se calcule automatiquement
          </p>
        </div>
        <p className="leading-relaxed text-gray-700"><strong>Fréquence :</strong> mettez à jour dès qu'une AG est planifiée ou s'est tenue.</p>
        <p className="leading-relaxed text-gray-700"><strong>Impact :</strong> l'équipe organise sa présence et optimise les opportunités commerciales.</p>
      </section>
    </div>
  );
};

export default GeneralAssemblyManagerTutorial;


